/**
 * [PURPOSE]: Pure mutation helpers for slot-recovery modal confirmation.
 *   Extracted from ClassResourcesPanel.js and RestPanel.js (Rule 22 / 31f) so
 *   both panels share one implementation and the integration test can drive
 *   REAL mutation logic (not source-scan assertions).
 * [WHY]: The onConfirm handlers in both panels were byte-identical duplicates,
 *   meaning a broken mutation would stay green under source-scan-only tests.
 *   Extracting here lets a49Phase2InvokeAnytime.test.js Group G assert the
 *   actual slot-decrement and pool-decrement output, including the no-input-
 *   mutation contract and the camelKey fallback branch.
 * [RULE 22]: Both panels import from here; tests import from here; no inline
 *   replicas.
 */

/**
 * Apply slot deltas to a spellSlots object.
 *
 * For each tier (level) in slotDeltas, decrements the `used` count by that
 * delta, floored at 0. Returns a new object — the input is NEVER mutated.
 *
 * @param {Object} currentSlots  - e.g. { 1: {used:2, max:3}, 2: {used:0, max:2} }
 * @param {Object} slotDeltas    - e.g. { 1: 2 }  (tier → slots-recovered count)
 * @returns {Object} new slots object
 */
export function applySlotDeltas(currentSlots, slotDeltas) {
  const next = { ...currentSlots };
  for (const [levelStr, delta] of Object.entries(slotDeltas || {})) {
    const level = parseInt(levelStr, 10);
    if (!Number.isFinite(level) || delta <= 0) continue;
    if (!next[level]) continue;
    next[level] = {
      ...next[level],
      used: Math.max(0, (next[level].used || 0) - delta),
    };
  }
  return next;
}

/**
 * Decrement the action's class pool `current` by 1.
 *
 * Finds the matching class entry by `action.classKey`, resolves the resource
 * key (snake_case `action.poolResourceId` first; camelCase fallback), and
 * returns a new state object with that pool decremented. Input is NEVER mutated.
 *
 * Works on any state shape that has a `.classes` array — both `character`
 * (used by ClassResourcesPanel via setCharacter) and `playerStats` (used by
 * RestPanel via setPlayerStats) qualify.
 *
 * @param {Object} state   - must have state.classes: Array<{className, ...pools}>
 * @param {Object} action  - must have action.classKey and action.poolResourceId
 * @returns {Object} new state object
 */
export function decrementActionPool(state, action) {
  if (!Array.isArray(state.classes)) return state;
  const camelKey = action.poolResourceId.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const nextClasses = state.classes.map(cls => {
    if (cls.className !== action.classKey) return cls;
    const key = cls[action.poolResourceId] != null ? action.poolResourceId : camelKey;
    const pool = cls[key] || { current: 0 };
    return { ...cls, [key]: { ...pool, current: Math.max(0, (pool.current || 0) - 1) } };
  });
  return { ...state, classes: nextClasses };
}
